import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      // THIS is why the page jumps to the top when the drawer closes on a phone.
      //
      // We are running from DialogOpenEvent, which showDialog dispatches AFTER
      // it has applied `position: fixed; top: -scrollY`. So at this exact moment
      // the document's real scroll offset is 0 - the shopper's position is
      // carried entirely by the negative top. The entry we are about to push
      // therefore records 0 as the page entry's scroll offset, and with
      // scrollRestoration on its default 'auto' the browser faithfully restores
      // that 0 when we call history.back() on close - a frame or two after
      // closeDialog correctly restored the real position. The browser wins,
      // because it acts last.
      //
      // Scroll restoration mode is a property of the entry that is current when
      // it is assigned, so it has to be set HERE, before the pushState, to land
      // on the page entry we will later travel back to. Setting it at close
      // time would only mark the drawer entry and change nothing.
      //
      // It is handed back to 'auto' in #handleHistoryClose once our own
      // traversal lands, so ordinary back-navigation to this page still restores
      // position. The flag lives on the session history entry for this document
      // only, so a real navigation away cannot inherit it.
      history.scrollRestoration = 'manual';
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  // Hand scroll restoration back to the browser, one task AFTER the traversal.
  //
  // Not inside the popstate turn. Whether a UA applies an entry's scroll
  // restoration before or after firing popstate is not consistent between
  // engines, and setting 'auto' there re-armed the very restoration the
  // 'manual' flag existed to suppress - the 0 recorded while the body was
  // locked. That is what made the X button jump to the top while the back
  // gesture did not. A task later the traversal has finished either way.
  #releaseScrollRestoration = () => {
    setTimeout(() => {
      history.scrollRestoration = 'auto';
    }, 0);
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    // Already traversed - the back gesture got here first, so there is no entry
    // left to pop. Still release the flag: leaving it 'manual' would silently
    // disable scroll restoration for ordinary back-navigation to this page.
    if (!history.state?.cartDrawerOpen) {
      this.#releaseScrollRestoration();
      return;
    }

    // closeDialog has already put the scroll back before dispatching the event
    // that got us here, so this is the position we want to end on.
    const restoreTo = window.scrollY;

    window.addEventListener(
      'popstate',
      () => {
        // Re-assert rather than trusting the flag. Both close paths now finish
        // at the same scroll position regardless of how the engine orders
        // popstate against scroll restoration.
        window.scrollTo({ top: restoreTo, behavior: 'instant' });
        this.#releaseScrollRestoration();
      },
      { once: true }
    );

    history.back();
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }

    this.#releaseScrollRestoration();
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
